/**
 * AIShield Customer Authentication & Session Manager (Stage 6A)
 * 
 * Strict Guarantees:
 * 1. Checks /api/v1/auth/me when entering customer mode.
 * 2. Uses HttpOnly cookie sessions through the /api proxy.
 * 3. Never stores passwords or session credentials in localStorage, sessionStorage, URLs, or console logs.
 * 4. Distinguishes unauthenticated 401 from network/server failures.
 * 5. Provides logout and clears customer state when logout succeeds.
 * 6. Clears cached customer data and cancels outstanding requests when account identity changes or authentication expires.
 * 7. Ignores stale responses from previous sessions via monotonically increasing session epoch.
 */

import {
  fetchMe,
  loginCustomer,
  registerCustomer,
  logoutCustomer,
  ApiClientError,
  type AccountResponse,
  type LoginPayload,
  type RegisterPayload,
  type RequestOptions,
} from "./apiClient.ts";

export type AuthStatus =
  | "idle"
  | "checking"
  | "authenticated"
  | "unauthenticated"
  | "error";

export interface AuthState {
  account: AccountResponse | null;
  status: AuthStatus;
  error: string | null;
  sessionEpoch: number;
}

export type AuthListener = (state: AuthState) => void;

export interface AuthApiAdapter {
  fetchMe: (options?: RequestOptions) => Promise<AccountResponse>;
  loginCustomer: (payload: LoginPayload, options?: RequestOptions) => Promise<AccountResponse>;
  registerCustomer: (payload: RegisterPayload, options?: RequestOptions) => Promise<AccountResponse>;
  logoutCustomer: (options?: RequestOptions) => Promise<{ status: string }>;
}

export class AuthManager {
  private _state: AuthState = {
    account: null,
    status: "idle",
    error: null,
    sessionEpoch: 0,
  };

  private _listeners = new Set<AuthListener>();
  private _checkAbortController: AbortController | null = null;
  private _sessionAbortController: AbortController = new AbortController();
  private _api: AuthApiAdapter;

  constructor(api?: Partial<AuthApiAdapter>) {
    this._api = {
      fetchMe: api?.fetchMe ?? fetchMe,
      loginCustomer: api?.loginCustomer ?? loginCustomer,
      registerCustomer: api?.registerCustomer ?? registerCustomer,
      logoutCustomer: api?.logoutCustomer ?? logoutCustomer,
    };
  }

  public getState(): AuthState {
    return { ...this._state };
  }

  public get sessionEpoch(): number {
    return this._state.sessionEpoch;
  }

  public get account(): AccountResponse | null {
    return this._accountSafeCopy();
  }

  public get status(): AuthStatus {
    return this._state.status;
  }

  public get error(): string | null {
    return this._state.error;
  }

  public subscribe(listener: AuthListener): () => void {
    this._listeners.add(listener);
    listener(this.getState());
    return () => {
      this._listeners.delete(listener);
    };
  }

  private _notify(): void {
    const snapshot = this.getState();
    for (const listener of this._listeners) {
      try {
        listener(snapshot);
      } catch (err) {
        // Prevent listener errors from breaking other subscribers
      }
    }
  }

  /**
   * Returns an AbortSignal tied to the current active customer session.
   * Automatically aborted whenever the account logs out or identity switches.
   */
  public getSessionSignal(): AbortSignal {
    return this._sessionAbortController.signal;
  }

  /**
   * Checks current customer session via GET /api/v1/auth/me.
   * Accurately distinguishes 401 (unauthenticated) from network/server errors.
   */
  public async checkSession(): Promise<AuthState> {
    // Cancel any previous in-flight check
    if (this._checkAbortController) {
      this._checkAbortController.abort();
    }
    this._checkAbortController = new AbortController();
    const signal = this._checkAbortController.signal;

    const requestEpoch = this._state.sessionEpoch;
    this._state = {
      ...this._state,
      status: "checking",
      error: null,
    };
    this._notify();

    try {
      const account = await this._api.fetchMe({ signal });

      // Discard response if session epoch changed while request was in-flight
      if (this._state.sessionEpoch !== requestEpoch) {
        return this.getState();
      }

      this._state = {
        ...this._state,
        account,
        status: "authenticated",
        error: null,
      };
      this._notify();
      return this.getState();
    } catch (err: unknown) {
      // If aborted by a newer check or logout, ignore
      if (err instanceof Error && err.name === "AbortError") {
        return this.getState();
      }

      if (this._state.sessionEpoch !== requestEpoch) {
        return this.getState();
      }

      if (err instanceof ApiClientError && err.status === 401) {
        // Unauthenticated session is NOT an operational error
        this._state = {
          ...this._state,
          account: null,
          status: "unauthenticated",
          error: null,
        };
        this._notify();
        return this.getState();
      }

      // Network connection failure or server error
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
          ? err.message
          : "Unable to reach authentication service";

      this._state = {
        ...this._state,
        account: null,
        status: "error",
        error: message,
      };
      this._notify();
      return this.getState();
    } finally {
      this._checkAbortController = null;
    }
  }

  /**
   * Logs in customer with credentials via POST /api/v1/auth/login.
   * Rotates session epoch and aborts previous customer requests.
   */
  public async login(payload: LoginPayload): Promise<AccountResponse> {
    // Invalidate prior session state and abort any active customer work
    this._cancelSessionWork();
    const nextEpoch = this._state.sessionEpoch + 1;

    try {
      const account = await this._api.loginCustomer(payload);

      this._state = {
        account,
        status: "authenticated",
        error: null,
        sessionEpoch: nextEpoch,
      };
      this._notify();
      return account;
    } catch (err: unknown) {
      // Login failed: state remains unauthenticated, does not advance epoch
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
          ? err.message
          : "Authentication failed";

      this._state = {
        ...this._state,
        account: null,
        status: "unauthenticated",
        error: message,
      };
      this._notify();
      throw err;
    }
  }

  /**
   * Registers a new customer account via POST /api/v1/auth/register.
   * Explicit contract: Registration creates the account but does NOT create a session.
   */
  public async register(payload: RegisterPayload): Promise<AccountResponse> {
    return await this._api.registerCustomer(payload);
  }

  /**
   * Logs out current customer session via POST /api/v1/auth/logout.
   * Cancels in-flight session requests, revokes session, and clears all customer state.
   */
  public async logout(options: RequestOptions = {}): Promise<void> {
    this._cancelSessionWork();
    const nextEpoch = this._state.sessionEpoch + 1;

    try {
      await this._api.logoutCustomer(options);
    } catch (err) {
      // Even if network logout errors, client-side session state must be cleared
    } finally {
      this._state = {
        account: null,
        status: "unauthenticated",
        error: null,
        sessionEpoch: nextEpoch,
      };
      this._notify();
    }
  }

  /**
   * Aborts active customer operations and resets the session abort controller.
   */
  private _cancelSessionWork(): void {
    if (this._checkAbortController) {
      this._checkAbortController.abort();
      this._checkAbortController = null;
    }
    this._sessionAbortController.abort();
    this._sessionAbortController = new AbortController();
  }

  private _accountSafeCopy(): AccountResponse | null {
    if (!this._state.account) return null;
    return { ...this._state.account };
  }
}

// Global shared singleton for the application
export const authManager = new AuthManager();
